import React, { useMemo, useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { fmtMoney, fmtMoney2, fmtPct } from "@/lib/api";
import { Calculator } from "lucide-react";

/**
 * Yield calculator — given a purchase price and the note's contractual
 * cash flow, projects total cash returned, profit, annualized yield,
 * weekly cash flow, and break-even week. Defaults to the note's asking price.
 */
export default function YieldCalculator({ note }) {
  const [price, setPrice] = useState(note?.asking_price || 0);
  const [defaultRate, setDefaultRate] = useState(0); // % of expected payments assumed lost
  const [recovery, setRecovery] = useState(0); // assumed recovery $ on default

  useEffect(() => {
    if (note?.asking_price) setPrice(note.asking_price);
  }, [note]);

  const result = useMemo(() => {
    if (!note) return null;
    // Payment-frequency-aware math. FinanceLocal stores `weekly_payment` and
    // `remaining_term_weeks` as label-agnostic PERIOD fields — their actual
    // meaning depends on `payment_frequency` (weekly / biweekly / monthly).
    const freq = String(note.payment_frequency || "weekly").toLowerCase();
    const periodsPerYear = freq === "monthly" ? 12 : freq === "biweekly" ? 26 : 52;
    const periodLabel = freq === "monthly" ? "months" : freq === "biweekly" ? "bi-weeks" : "weeks";
    const perPeriodShort = freq === "monthly" ? "mo" : freq === "biweekly" ? "biwk" : "wk";
    const periods = Math.max(1, note.remaining_term_weeks || 1);
    const perPeriod = Number(note.weekly_payment || 0);
    const grossPayments = perPeriod * periods;
    const expectedPayments = grossPayments * (1 - defaultRate / 100) + Number(recovery || 0);
    const purchase = Math.max(1, Number(price) || 1);
    const profit = expectedPayments - purchase;
    const years = periods / periodsPerYear;
    const annualYield = years > 0 ? (profit / purchase) / years * 100 : 0;
    const cashOnCash = (perPeriod * periodsPerYear * (1 - defaultRate / 100)) / purchase * 100;
    const roi = (profit / purchase) * 100;
    const breakEvenPeriod = perPeriod > 0 ? Math.ceil(purchase / (perPeriod * (1 - defaultRate / 100 || 1))) : null;
    const monthlyCash  = perPeriod * (periodsPerYear / 12) * (1 - defaultRate / 100);
    const weeklyCash   = perPeriod * (periodsPerYear / 52) * (1 - defaultRate / 100);

    return {
      grossPayments, expectedPayments, profit,
      annualYield, cashOnCash, roi, breakEvenPeriod, monthlyCash, weeklyCash,
      periods, perPeriod, periodsPerYear, periodLabel, perPeriodShort,
    };
  }, [note, price, defaultRate, recovery]);

  if (!note || !result) return null;

  const discount = ((note.asking_price - Number(price)) / note.asking_price) * 100;

  return (
    <div data-testid="yield-calculator" className="border border-border bg-card">
      <div className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calculator className="w-4 h-4 text-primary" />
          <div className="font-overline">Yield Calculator</div>
        </div>
        <div className="font-overline text-muted-foreground hidden sm:block">Interactive · No Login Required</div>
      </div>

      <div className="p-6 space-y-6">
        {/* Purchase price */}
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <Label className="font-overline">Your Purchase Price</Label>
            <div className={`text-xs font-mono-data ${discount > 0 ? "text-primary" : "text-muted-foreground"}`}>
              {discount > 0 ? `${discount.toFixed(1)}% off ask` : discount < 0 ? `${Math.abs(discount).toFixed(1)}% over ask` : "at ask"}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-mono-data text-muted-foreground">$</span>
            <Input
              type="number"
              data-testid="calc-price-input"
              value={price}
              onChange={(e) => setPrice(Number(e.target.value))}
              className="rounded-none font-mono-data text-lg"
            />
          </div>
          <div className="mt-3">
            <Slider
              data-testid="calc-price-slider"
              min={Math.max(1, Math.round(note.asking_price * 0.4))}
              max={Math.round(note.asking_price * 1.1)}
              step={50}
              value={[Number(price) || 0]}
              onValueChange={([v]) => setPrice(v)}
              className="mt-2"
            />
            <div className="flex justify-between mt-2 text-[10px] font-mono-data text-muted-foreground">
              <span>{fmtMoney(Math.max(1, Math.round(note.asking_price * 0.4)))}</span>
              <span>Ask {fmtMoney(note.asking_price)}</span>
              <span>{fmtMoney(Math.round(note.asking_price * 1.1))}</span>
            </div>
          </div>
        </div>

        {/* Risk assumptions */}
        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
          <div>
            <Label className="font-overline mb-2 block">Assumed Default Rate</Label>
            <div className="flex items-center gap-2">
              <Input type="number" min={0} max={100} value={defaultRate}
                onChange={(e) => setDefaultRate(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                className="rounded-none font-mono-data" data-testid="calc-default-rate" />
              <span className="text-muted-foreground">%</span>
            </div>
          </div>
          <div>
            <Label className="font-overline mb-2 block">Recovery on Default</Label>
            <div className="flex items-center gap-2">
              <span className="font-mono-data text-muted-foreground">$</span>
              <Input type="number" min={0} value={recovery}
                onChange={(e) => setRecovery(Math.max(0, Number(e.target.value) || 0))}
                className="rounded-none font-mono-data" data-testid="calc-recovery" />
            </div>
          </div>
        </div>

        {/* Headline result */}
        <div className="grid grid-cols-2 border border-border grid-borders">
          <Stat label="Projected Annual Yield" value={fmtPct(result.annualYield)} highlight testId="calc-annual-yield" />
          <Stat label="Total Profit" value={fmtMoney(result.profit)} testId="calc-total-profit" />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 border border-border grid-borders">
          <Stat label={`Per-Period Cash (${result.perPeriodShort})`} value={fmtMoney2(result.perPeriod * (1 - defaultRate / 100))} dense testId="calc-per-period" />
          <Stat label="Monthly Cash" value={fmtMoney(result.monthlyCash)} dense testId="calc-monthly" />
          <Stat label="ROI" value={fmtPct(result.roi)} dense testId="calc-roi" />
          <Stat label="Cash-on-Cash" value={fmtPct(result.cashOnCash)} dense testId="calc-coc" />
        </div>

        <div className="border border-border divide-y divide-border text-sm">
          <Row label="Contractual Total Payments" value={fmtMoney(result.grossPayments)} />
          <Row label="Expected After Default Assumption" value={fmtMoney(result.expectedPayments)} />
          <Row label="Remaining Term" value={`${result.periods} ${result.periodLabel} (${(result.periods / result.periodsPerYear).toFixed(1)} yrs)`} />
          <Row label={`Break-even ${result.perPeriodShort}`} value={result.breakEvenPeriod ? `#${result.breakEvenPeriod}` : "—"} />
        </div>

        <p className="text-[11px] text-muted-foreground leading-relaxed border-t border-border pt-4">
          Projections are illustrative only and based on contractual cash flow with your selected default assumption.
          Actual returns depend on borrower payment behavior, collection costs, taxes, and other factors.
        </p>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight, dense, testId }) {
  return (
    <div className={`${dense ? "p-3" : "p-5"}`} data-testid={testId}>
      <div className="font-overline text-muted-foreground">{label}</div>
      <div className={`font-mono-data ${dense ? "text-lg" : "text-2xl sm:text-3xl"} mt-1 font-light tracking-tighter ${highlight ? "text-primary" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between px-4 py-2">
      <span className="font-overline text-muted-foreground">{label}</span>
      <span className="font-mono-data">{value}</span>
    </div>
  );
}
